/**
 * Pure straight-line book-depreciation model for
 * `/tools/straight-line-depreciation-calculator`.
 *
 * Definition (OpenStax Principles of Financial Accounting, Vol. 1, §11.3 —
 * "Explain and Apply Depreciation Methods to Allocate Capitalized Costs":
 * https://openstax.org/books/principles-financial-accounting/pages/11-3-explain-and-apply-depreciation-methods-to-allocate-capitalized-costs):
 *
 * - **Depreciable base** = cost − residual value
 * - **Monthly expense** = base ÷ useful life in whole months
 *
 * The guide this tool accompanies
 * (`blog/2026-04-03-straight-line-depreciation-formula-examples-how-to-calculate.md`)
 * teaches the same formula with a 3,000 cost / 200 residual / 48-month
 * example: 48 × 58.33 = 2,799.84, leaving 0.16 unallocated. This model
 * closes that gap with an explicit rounding policy (below) so the displayed
 * schedule and any exported entries always agree.
 *
 * Full-month convention: depreciation starts with a full monthly charge in
 * the reader-chosen first month and runs for the whole useful life, with
 * each charge posted on the month-end date. This is one common book policy
 * for internal reporting — not a universal rule: other book policies prorate
 * the acquisition/disposal months, and tax depreciation (MACRS/bonus) uses
 * different recovery periods and conventions entirely. This module models
 * book depreciation only.
 *
 * Rounding policy (exact, remainder-in-final-month): all arithmetic runs in
 * integer minor units (cents for 2-decimal currencies). No intermediate
 * value is rounded before the schedule is formed. The regular-month expense
 * is `floor(base ÷ months)` in minor units; the final month takes the
 * remainder `base − regular × (months − 1)`, which is always ≥ the regular
 * charge and ≥ 0. Flooring (rather than rounding to nearest) is what keeps
 * the final charge nonnegative even when the base holds fewer minor units
 * than there are months (e.g. a few cents spread over many months collapse
 * to zero-expense early months plus a small final charge).
 *
 * Bounds (finite by design):
 *
 * - cost / residual: 0 … 999,999,999,999 major units, at most the currency's
 *   fraction digits; minor-unit totals stay below Number.MAX_SAFE_INTEGER.
 * - useful life: an integer of 1 … 1,200 months (100 years).
 * - first month: `YYYY-MM` in 1900-01 … 2100-12.
 *
 * Currency is declared, never converted: one asset, one currency. Precision
 * follows ISO 4217 minor units for well-known codes (JPY/KRW/VND and peers
 * at 0, BHD/JOD/KWD/OMR/TND and peers at 3, everything else defaults to 2).
 * Amounts carrying more fraction digits than the currency allows are
 * rejected as `unsupported-precision`. Localized prose stays out of this
 * module; month-end dates are computed as pure calendar dates (no Date
 * object, no timezone) in `YYYY-MM-DD` form.
 *
 * Results distinguish incomplete (blank fields), invalid (typed per-field
 * errors below), zero-expense (zero cost, or cost equal to residual — an
 * explicit all-zero schedule, with no division performed) and ok.
 */

import { daysInMonth } from "@/lib/civil-date";
import {
  parseNumericInput,
  type NumericDomain,
  type NumericParseError,
} from "@/lib/numeric-input";

/** Longest supported schedule: 100 years of monthly rows. */
const MAX_USEFUL_LIFE_MONTHS = 1_200;

/** Largest supported amount in major currency units (just under 1e12). */
const MAX_AMOUNT_MAJOR = 999_999_999_999;

/** Earliest supported first depreciation month. */
const MIN_FIRST_MONTH = "1900-01";

/** Latest supported first depreciation month. */
const MAX_FIRST_MONTH = "2100-12";

/** ISO codes using zero fraction digits (ISO 4217 minor unit 0). */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "CLP",
  "ISK",
  "JPY",
  "KRW",
  "UGX",
  "VND",
  "XAF",
  "XOF",
  "XPF",
]);

/** ISO codes using three fraction digits (ISO 4217 minor unit 3). */
const THREE_DECIMAL_CURRENCIES = new Set([
  "BHD",
  "IQD",
  "JOD",
  "KWD",
  "LYD",
  "OMR",
  "TND",
]);

/** Minor-unit cap covering the max major amount at 3 fraction digits. */
const MAX_MINOR = BigInt(MAX_AMOUNT_MAJOR) * 1_000n;

/** Useful life in whole months: an integer of 1 … 1,200. */
const USEFUL_LIFE_DOMAIN: NumericDomain = {
  min: 1,
  max: MAX_USEFUL_LIFE_MONTHS,
  integer: true,
};

/**
 * Reviewed worked example from the April 3 guide. Loading these strings must
 * produce a 48-row schedule totalling 2,800 with a final carrying value of
 * 200, a regular expense of 58.33 and a final charge of 58.49.
 */
const STRAIGHT_LINE_EXAMPLE = {
  cost: "3000",
  residual: "200",
  usefulLifeMonths: "48",
  firstMonth: "2026-01",
  currency: "USD",
} as const;

export type StraightLineDepreciationField =
  | "cost"
  | "residual"
  | "usefulLifeMonths"
  | "firstMonth"
  | "currency";

export type StraightLineDepreciationError =
  | NumericParseError
  | "invalid-month"
  | "invalid-currency"
  | "unsupported-precision"
  | "residual-above-cost";

export type StraightLineDepreciationFieldError = {
  field: StraightLineDepreciationField;
  error: StraightLineDepreciationError;
};

/** Raw editable field texts the calculator holds in React state. */
export interface StraightLineDepreciationRawInputs {
  /** Purchase price / cost basis, same currency as residual. */
  cost: string;
  /** Expected end-of-life value, same currency as cost. */
  residual: string;
  /** Useful life in whole months. */
  usefulLifeMonths: string;
  /** First depreciation month as `YYYY-MM`. */
  firstMonth: string;
  /** Declared ISO 4217 currency code (e.g. `USD`); display-only. */
  currency: string;
}

/** Validated numbers behind a schedule, in exact integer minor units. */
export interface StraightLineDepreciationNumbers {
  costMinor: number;
  residualMinor: number;
  usefulLifeMonths: number;
  /** Normalized first month as `YYYY-MM`. */
  firstMonth: string;
  /** Normalized (uppercase) declared currency. */
  currency: string;
  /** Fraction digits used for the currency (0, 2 or 3). */
  fractionDigits: number;
}

/** One reconciled schedule row, posted on the month-end date. */
export interface StraightLineDepreciationRow {
  /** 1-based position in the schedule. */
  index: number;
  /** Calendar month as `YYYY-MM`. */
  month: string;
  /** Deterministic month-end posting date as `YYYY-MM-DD`. */
  date: string;
  /** This month's expense in exact minor units. */
  expenseMinor: number;
  /** This month's expense in major units (minor ÷ 10^digits). */
  expense: number;
  /** Running total expense in exact minor units. */
  accumulatedMinor: number;
  /** Running total expense in major units. */
  accumulated: number;
  /** cost − accumulated, in exact minor units. */
  carryingMinor: number;
  /** cost − accumulated, in major units. */
  carrying: number;
}

/** Exact reconciliation summary for an `ok` schedule. */
export interface StraightLineDepreciationSummary {
  /** cost − residual in minor units; total expense over the schedule. */
  depreciableBaseMinor: number;
  depreciableBase: number;
  /** Months 1 … n−1 charge in minor units: floor(base ÷ months). */
  regularExpenseMinor: number;
  regularExpense: number;
  /** Final-month remainder charge in minor units. */
  finalExpenseMinor: number;
  finalExpense: number;
}

export type StraightLineDepreciationResult =
  | {
      status: "incomplete";
      fieldErrors: StraightLineDepreciationFieldError[];
    }
  | {
      status: "invalid";
      fieldErrors: StraightLineDepreciationFieldError[];
    }
  | {
      status: "zero-expense";
      numbers: StraightLineDepreciationNumbers;
      schedule: StraightLineDepreciationRow[];
      fieldErrors: [];
    }
  | {
      status: "ok";
      numbers: StraightLineDepreciationNumbers;
      summary: StraightLineDepreciationSummary;
      schedule: StraightLineDepreciationRow[];
      fieldErrors: [];
    };

/** Fraction digits for a normalized (uppercase) currency code. */
export function fractionDigitsForCurrency(currency: string): number {
  if (ZERO_DECIMAL_CURRENCIES.has(currency)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(currency)) return 3;
  return 2;
}

/**
 * Render exact integer minor units as an invariant decimal with exactly
 * `digits` fraction places. Pure string arithmetic — no float step, so
 * 5,849 minor at 2 digits is always "58.49", never "58.489999…".
 */
export function formatMinorUnits(minor: number, digits: number): string {
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  const base = 10 ** digits;
  const intPart = Math.floor(abs / base);
  if (digits === 0) return `${sign}${intPart}`;
  const frac = String(abs % base).padStart(digits, "0");
  return `${sign}${intPart}.${frac}`;
}

/**
 * Localized digits of exact minor units: {@link formatMinorUnits} is the only
 * arithmetic, and `Intl` only groups and localizes its digits. Returns a
 * formatter so a caller rendering many figures builds one `NumberFormat`.
 */
export function minorUnitDigitsFormatter(
  digits: number,
  locale: string,
): (minor: number) => string {
  let formatter: Intl.NumberFormat | null;
  try {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  } catch {
    formatter = null;
  }
  return (minor) => {
    const machine = formatMinorUnits(minor, digits);
    return formatter ? formatter.format(Number(machine)) : machine;
  };
}

/** Reasons a decimal amount can fail {@link parseAmountToMinor}. */
export type AmountParseError = NumericParseError | "unsupported-precision";

type AmountParse =
  | { ok: true; minor: number }
  | { ok: false; error: AmountParseError };

const AMOUNT_RE = /^(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

/**
 * Parse a plain decimal amount into exact integer minor units without any
 * floating-point step. A leading `-` is reported as `below-minimum` (when
 * the magnitude is otherwise well-formed) so the UI can point at the sign;
 * more fraction digits than the currency allows are `unsupported-precision`;
 * malformed or non-finite text is `not-a-number`; amounts above the
 * documented bound are `above-maximum`.
 */
export function parseAmountToMinor(
  raw: string,
  fractionDigits: number,
): AmountParse {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false, error: "blank" };
  let unsigned = trimmed;
  let negative = false;
  if (unsigned.startsWith("-")) {
    negative = true;
    unsigned = unsigned.slice(1);
  } else if (unsigned.startsWith("+")) {
    unsigned = unsigned.slice(1);
  }
  const match = AMOUNT_RE.exec(unsigned);
  if (!match) return { ok: false, error: "not-a-number" };
  const intPart = match[1] ?? "";
  const fracPart = match[2] ?? "";
  const exponent = match[3] === undefined ? 0 : Number(match[3]);
  if (!Number.isInteger(exponent) || Math.abs(exponent) > 1_000_000) {
    return { ok: false, error: "not-a-number" };
  }
  const digits = `${intPart}${fracPart}`;
  // Decimals the value actually carries after applying the exponent.
  const valueFractionDigits = fracPart.length - exponent;
  if (valueFractionDigits > fractionDigits) {
    return { ok: false, error: "unsupported-precision" };
  }
  // Cheap magnitude guard before materializing the BigInt: MAX_MINOR is
  // 999,999,999,999,000 (15 digits), so anything needing 17+ digits is out
  // of bounds without computing it.
  const significant = digits.replace(/^0+/, "");
  if (
    significant !== "" &&
    significant.length + fractionDigits - valueFractionDigits > 16
  ) {
    return { ok: false, error: "above-maximum" };
  }
  const scale = 10n ** BigInt(fractionDigits - valueFractionDigits);
  let minor: bigint;
  try {
    minor = BigInt(digits === "" ? "0" : digits) * scale;
  } catch {
    return { ok: false, error: "not-a-number" };
  }
  if (minor > MAX_MINOR) return { ok: false, error: "above-maximum" };
  const minorNumber = Number(minor);
  if (!Number.isSafeInteger(minorNumber)) {
    return { ok: false, error: "above-maximum" };
  }
  if (negative) {
    // Zero magnitude with a sign ("-0", "-0.00") is still an entered zero.
    if (minorNumber === 0) return { ok: true, minor: 0 };
    return { ok: false, error: "below-minimum" };
  }
  return { ok: true, minor: minorNumber };
}

const FIRST_MONTH_RE = /^(\d{4})-(\d{2})$/;

function parseFirstMonth(raw: string): {
  ok: boolean;
  year: number;
  month: number;
  normalized: string;
} {
  const trimmed = raw.trim();
  const match = FIRST_MONTH_RE.exec(trimmed);
  if (!match) return { ok: false, year: 0, month: 0, normalized: trimmed };
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12 ||
    trimmed < MIN_FIRST_MONTH ||
    trimmed > MAX_FIRST_MONTH
  ) {
    return { ok: false, year: 0, month: 0, normalized: trimmed };
  }
  return { ok: true, year, month, normalized: trimmed };
}

export function parseCurrency(raw: string): {
  ok: boolean;
  normalized: string;
} {
  const trimmed = raw.trim();
  if (/^[A-Za-z]{3}$/.test(trimmed)) {
    return { ok: true, normalized: trimmed.toUpperCase() };
  }
  return { ok: false, normalized: trimmed };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function toMajor(minor: number, fractionDigits: number): number {
  return minor / 10 ** fractionDigits;
}

function buildSchedule(
  numbers: StraightLineDepreciationNumbers,
  monthlyExpenseMinor: readonly number[],
): StraightLineDepreciationRow[] {
  const start = parseFirstMonth(numbers.firstMonth);
  const rows: StraightLineDepreciationRow[] = [];
  let accumulatedMinor = 0;
  for (let i = 0; i < monthlyExpenseMinor.length; i += 1) {
    const expenseMinor = monthlyExpenseMinor[i] ?? 0;
    accumulatedMinor += expenseMinor;
    const totalMonths = start.month - 1 + i;
    const year = start.year + Math.floor(totalMonths / 12);
    const month = (totalMonths % 12) + 1;
    const day = daysInMonth(year, month);
    const monthText = `${year}-${pad2(month)}`;
    const carryingMinor = numbers.costMinor - accumulatedMinor;
    rows.push({
      index: i + 1,
      month: monthText,
      date: `${monthText}-${pad2(day)}`,
      expenseMinor,
      expense: toMajor(expenseMinor, numbers.fractionDigits),
      accumulatedMinor,
      accumulated: toMajor(accumulatedMinor, numbers.fractionDigits),
      carryingMinor,
      carrying: toMajor(carryingMinor, numbers.fractionDigits),
    });
  }
  return rows;
}

function buildZeroSchedule(
  numbers: StraightLineDepreciationNumbers,
): StraightLineDepreciationRow[] {
  return buildSchedule(
    numbers,
    new Array<number>(numbers.usefulLifeMonths).fill(0),
  );
}

/**
 * Compute the reconciled monthly schedule from raw editable inputs.
 *
 * Blank fields → `incomplete`. Malformed dates/currencies, precision,
 * bound or domain violations, and residual above cost → `invalid` with
 * per-field reasons. Equal cost and residual (or zero cost) → an explicit
 * `zero-expense` schedule with no division. Otherwise `ok` with a schedule
 * whose expenses total exactly cost − residual and whose final carrying
 * value is exactly the residual.
 */
export function calculateStraightLineDepreciation(
  raw: StraightLineDepreciationRawInputs,
): StraightLineDepreciationResult {
  const fieldErrors: StraightLineDepreciationFieldError[] = [];

  const currencyParsed = parseCurrency(raw.currency);
  if (raw.currency.trim() === "") {
    fieldErrors.push({ field: "currency", error: "blank" });
  } else if (!currencyParsed.ok) {
    fieldErrors.push({ field: "currency", error: "invalid-currency" });
  }
  const fractionDigits = currencyParsed.ok
    ? fractionDigitsForCurrency(currencyParsed.normalized)
    : 2;

  const costParsed = parseAmountToMinor(raw.cost, fractionDigits);
  if (!costParsed.ok) {
    fieldErrors.push({ field: "cost", error: costParsed.error });
  }
  const residualParsed = parseAmountToMinor(raw.residual, fractionDigits);
  if (!residualParsed.ok) {
    fieldErrors.push({ field: "residual", error: residualParsed.error });
  }

  const lifeParsed = parseNumericInput(raw.usefulLifeMonths, USEFUL_LIFE_DOMAIN);
  if (!lifeParsed.ok) {
    fieldErrors.push({ field: "usefulLifeMonths", error: lifeParsed.error });
  }

  const monthParsed = parseFirstMonth(raw.firstMonth);
  if (raw.firstMonth.trim() === "") {
    fieldErrors.push({ field: "firstMonth", error: "blank" });
  } else if (!monthParsed.ok) {
    fieldErrors.push({ field: "firstMonth", error: "invalid-month" });
  }

  if (fieldErrors.some((entry) => entry.error === "blank")) {
    return { status: "incomplete", fieldErrors };
  }
  if (fieldErrors.length > 0) {
    return { status: "invalid", fieldErrors };
  }
  if (!costParsed.ok || !residualParsed.ok || !lifeParsed.ok) {
    return { status: "invalid", fieldErrors };
  }

  if (residualParsed.minor > costParsed.minor) {
    return {
      status: "invalid",
      fieldErrors: [{ field: "residual", error: "residual-above-cost" }],
    };
  }

  const numbers: StraightLineDepreciationNumbers = {
    costMinor: costParsed.minor,
    residualMinor: residualParsed.minor,
    usefulLifeMonths: lifeParsed.value,
    firstMonth: monthParsed.normalized,
    currency: currencyParsed.normalized,
    fractionDigits,
  };

  if (costParsed.minor === residualParsed.minor) {
    return {
      status: "zero-expense",
      numbers,
      schedule: buildZeroSchedule(numbers),
      fieldErrors: [],
    };
  }

  const baseMinor = costParsed.minor - residualParsed.minor;
  const months = lifeParsed.value;
  const regularMinor = Math.floor(baseMinor / months);
  const finalMinor = baseMinor - regularMinor * (months - 1);
  const monthly = new Array<number>(months).fill(regularMinor);
  monthly[months - 1] = finalMinor;

  return {
    status: "ok",
    numbers,
    summary: {
      depreciableBaseMinor: baseMinor,
      depreciableBase: toMajor(baseMinor, fractionDigits),
      regularExpenseMinor: regularMinor,
      regularExpense: toMajor(regularMinor, fractionDigits),
      finalExpenseMinor: finalMinor,
      finalExpense: toMajor(finalMinor, fractionDigits),
    },
    schedule: buildSchedule(numbers, monthly),
    fieldErrors: [],
  };
}

/** Empty form state — blanks, never silent zeros. */
export function emptyStraightLineDepreciationInputs(): StraightLineDepreciationRawInputs {
  return {
    cost: "",
    residual: "",
    usefulLifeMonths: "",
    firstMonth: "",
    currency: "",
  };
}

/** Reviewed worked-example inputs (3,000 / 200 / 48 months, USD). */
export function exampleStraightLineDepreciationInputs(): StraightLineDepreciationRawInputs {
  return { ...STRAIGHT_LINE_EXAMPLE };
}
